'use client';

import { useEffect, useRef, useState } from 'react';

type Stat = {
  label: string;
  value: number;
  suffix?: string;
  prefix?: string;
};

const STATS: Stat[] = [
  { label: 'Live SKUs', value: 20000, suffix: '+' },
  { label: 'Brands', value: 300, suffix: '+' },
  { label: 'Countries', value: 100, suffix: '+' },
  { label: 'Since', value: 2016 },
];

const DURATION_MS = 1600;

export function AboutHeroStats() {
  const ref = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (started) return;

    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    if (reduceMotion) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStarted(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setStarted(true);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: 0.4 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [started]);

  return (
    <div
      ref={ref}
      className="relative overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-6 sm:p-8"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(70% 90% at 100% 0%, rgba(254, 119, 67, 0.10) 0%, rgba(254, 119, 67, 0) 55%)',
        }}
      />
      <dl className="relative grid grid-cols-2 gap-x-8 gap-y-7 sm:gap-x-6 sm:gap-y-10">
        {STATS.map((stat) => (
          <CountUpStat key={stat.label} stat={stat} run={started} />
        ))}
      </dl>
    </div>
  );
}

function CountUpStat({ stat, run }: { stat: Stat; run: boolean }) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!run) return;

    const start = performance.now();
    let raf = 0;

    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / DURATION_MS, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(stat.value * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [run, stat.value]);

  const formatted =
    stat.value >= 1000 && stat.label !== 'Since'
      ? display.toLocaleString('en-US')
      : String(display);

  return (
    <div>
      <dd className="font-display tabular text-2xl font-bold leading-none text-[var(--text-secondary)] sm:text-4xl md:text-5xl">
        {stat.prefix}
        {formatted}
        {stat.suffix}
      </dd>
      <dt className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--accent)] sm:text-xs">
        {stat.label}
      </dt>
    </div>
  );
}
